import { isMotusEnv } from 'app/settings/defaultSetting'
import { AxiosError, AxiosInstance } from 'axios'
import dayjs from 'utils/dayjs'
import { docCookies } from 'utils/docCookies'

// Extend Axios request config to include service name
declare module 'axios' {
  export interface AxiosRequestConfig {
    serviceName?: string
  }
}

// Get user type from local storage
const getApiConfig = () => {
  const baseUrl = isMotusEnv
    ? import.meta.env.VITE_APP_MOTUS_API_BASE_URL ||
      import.meta.env.VITE_APP_API_BASE_URL
    : import.meta.env.VITE_APP_API_BASE_URL
  const accessToken = docCookies.getItem('motus_access_token')

  return {
    baseUrl,
    accessToken,
    isMotus: isMotusEnv
  }
}

// Extract service error function
const extractErrorFunction = (serviceName: string, stack = '') => {
  const extractErrorSourceMethodRegex = new RegExp(
    `at\\s+(?:async\\s+)?${serviceName}\\.(\\w+)\\s+\\(`
  )
  const match = stack.match(extractErrorSourceMethodRegex)
  return match ? match[1] : 'UNKNOWN_SERVICE'
}

// Axios error handler with logging
const handleAxiosError = (error: AxiosError) => {
  const { serviceName } = error.config || {}
  const errorFunction = serviceName
    ? extractErrorFunction(serviceName, new Error().stack)
    : undefined
  const service = serviceName ?? 'UNKNOWN_SERVICE'
  const requestMethod = error.config?.method?.toUpperCase()
  const responseStatus = error.response?.status || 'Network Error'
  const headers = error.config?.headers.toJSON()
  delete headers?.Authorization

  console.error(
    `[${service}] API Error: ${requestMethod} /${error.config?.url} - ${responseStatus}`,
    {
      function: {
        service,
        method: errorFunction
      },
      request: {
        method: requestMethod,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        headers
      },
      response: {
        status: responseStatus,
        statusText: error.response?.statusText,
        data: error.response?.data
      },
      error: {
        message: error.message
      },
      metadata: {
        timestamp: new Date().toISOString()
      }
    }
  )

  return Promise.reject(error)
}

type SetUpInterceptorsParams = {
  instance: AxiosInstance
} & (
  | {
      interceptRequest?: false
      skipVersion?: false
    }
  | {
      interceptRequest: true
      skipVersion?: boolean
    }
)

/**
 * Setup axios instance interceptors
 * If `interceptRequest` is true, it will setup request interceptor for base url and auth management
 * @param instance Axios instance
 * @param interceptRequest Boolean to setup request interceptor
 * @param skipVersion Boolean to remove v6 from API route. Can only be true if `interceptRequest` is true
 */
export const setUpInterceptors = ({
  instance,
  interceptRequest,
  skipVersion
}: SetUpInterceptorsParams) => {
  if (interceptRequest) {
    instance.interceptors.request.use(
      (config) => {
        const { baseUrl, accessToken, isMotus } = getApiConfig()
        config.baseURL = skipVersion ? baseUrl.replace('v6/', '') : baseUrl
        if (config?.headers) {
          config.headers['request_send_at'] = dayjs().format()
        }
        // Respect an explicit Authorization (e.g. the refresh token on /login)
        if (isMotus && accessToken && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Token token=${accessToken}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }
  instance.interceptors.response.use((response) => response, handleAxiosError)
}

/**
 * Create axios instance with name for logging
 * @param instance Axios instance
 * @param serviceName Service name used for logging
 * @returns Modified axios instance
 */
export const createAxiosInstanceWithName = (
  instance: AxiosInstance,
  serviceName: string
): AxiosInstance => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wrapper: any = Object.create(instance)
  ;['get', 'post', 'put', 'delete', 'patch'].forEach((method) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const original = (instance as any)[method].bind(instance)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    wrapper[method] = (...args: any[]) => {
      const configIndex = method === 'get' || method === 'delete' ? 1 : 2
      if (!args[configIndex]) args[configIndex] = {}
      args[configIndex].serviceName = serviceName
      return original(...args)
    }
  })
  return wrapper
}
